import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import { httpPatchAssetsByAssetId } from '@iconik-io/iconik-api/src/services/assets';
import { unregisterFile } from '@iconik-io/shared-lib/src/uploader/file_registry';
import { getSourceStream } from '@iconik-io/shared-lib/src/uploader/get_source_stream';
import { HttpClient } from '@iconik-io/shared-lib/src/uploader/http_client';
import { UploaderStateStorageRedux } from '@iconik-io/shared-lib/src/uploader/redux_state_storage';
import { CachedStorageOptionsProvider } from '@iconik-io/uploader/src/cached_storage_options_provider';
import { CollectionsService } from '@iconik-io/uploader/src/collections_service';
import { JobsService } from '@iconik-io/uploader/src/job_service/job_service';
import { IconikUploadScheduler } from '@iconik-io/uploader/src/scheduler';
import type {
  UploaderState,
  UploaderStateStorageInterface,
  UploadOptions,
} from '@iconik-io/uploader/src/state';
import { JobIdStorageMemory } from '@iconik-io/uploader/src/storage/job_id_memory';
import type { StorageOptions } from '@iconik-io/uploader/src/types';

import { deleteFileComponents } from '../file_components';
import { createApiClient } from './api_client';
import type { UploadRequest } from './upload_requests';
import {
  attachUploadSideEffects,
  type UploadSideEffects,
  type UploadSideEffectsStore,
} from './upload_side_effects_listener';

const logger = DebugLogger.getInstance('web_uploader');

/**
 * Storage settings are generated as a loose index signature, so read the flags
 * the uploader cares about by checking their runtime type.
 */
const readBooleanSetting = (
  settings: object | undefined,
  key: string,
): boolean | undefined => {
  const value: unknown = settings
    ? Object.getOwnPropertyDescriptor(settings, key)?.value
    : undefined;
  return typeof value === 'boolean' ? value : undefined;
};

/**
 * Runs web's uploads on @iconik-io/uploader. The scheduler keeps its state in
 * the app store, where the uploads panel reads it, and uploads are addressed
 * by the id of that state.
 */
export class UploaderService {
  #store: UploadSideEffectsStore;

  #stateStorage: UploaderStateStorageInterface;

  #scheduler: IconikUploadScheduler;

  #jobsService: JobsService;

  #storageOptionsProvider: CachedStorageOptionsProvider;

  #sideEffects: UploadSideEffects;

  constructor(store: UploadSideEffectsStore) {
    this.#store = store;
    const apiClient = createApiClient(store.dispatch);
    const collectionsService = new CollectionsService(apiClient);
    // Uploads live for the tab only. A File picked through an input, dropped or
    // pasted cannot be revived after a reload, so the store is not persisted.
    this.#stateStorage = new UploaderStateStorageRedux(store);
    this.#storageOptionsProvider = new CachedStorageOptionsProvider(apiClient);

    this.#scheduler = new IconikUploadScheduler(
      apiClient,
      new HttpClient(),
      collectionsService,
      getSourceStream,
      this.#stateStorage,
      {
        getStorageOptions: this.#getStorageOptions,
      },
    );

    this.#jobsService = new JobsService(
      this.#scheduler,
      apiClient,
      new JobIdStorageMemory(),
      { clientName: 'Web' },
    );

    this.#jobsService.on('log', ({ level, message }) => {
      logger.debug(`[JobsService] [${level}] ${message}`);
    });
    collectionsService.on('log', ({ level, message }) => {
      logger.debug(`[CollectionsService] [${level}] ${message}`);
    });

    this.#sideEffects = attachUploadSideEffects({
      scheduler: this.#scheduler,
      store,
    });
  }

  /**
   * Storages are already in the app state, so they are served from there
   * instead of fetched again. A share only exposes its upload storage on the share
   * itself.
   */
  #getStorageOptions = (storageId: string): Promise<StorageOptions> => {
    const state = this.#store.getState();
    const share = state.share.share;
    const shareStorage = share.upload_storage;
    if (share.id && shareStorage?.id === storageId) {
      return Promise.resolve({
        id: shareStorage.id,
        method: shareStorage.method,
        add_uuid_to_filenames: shareStorage.add_uuid_to_filenames,
        presign_md5_checksum:
          share.upload_storage_presign_md5_checksum ?? undefined,
        // TODO: RP-2007; Remove isShareUser when API differences are resolved.
        // https://linear.app/iconik/issue/RP-2007/merge-apis-for-checking-if-file-exists-in-storage
        isShareUser: true,
      });
    }

    const matchedStorage = state.storageMatchingReducer.filesStorageMatched;
    const storage =
      state.entities.storages[storageId] ??
      (matchedStorage?.id === storageId ? matchedStorage : undefined);
    if (!storage) {
      return this.#storageOptionsProvider.getStorageOptions(storageId);
    }
    return Promise.resolve({
      id: storage.id,
      method: storage.method,
      add_uuid_to_filenames: readBooleanSetting(
        storage.settings,
        'add_uuid_to_filenames',
      ),
      presign_md5_checksum: readBooleanSetting(
        storage.settings,
        'presign_md5_checksum',
      ),
    });
  };

  /** Transfer jobs are only reported for users allowed to write them. */
  #syncJobsService() {
    if (this.#store.getState().rolesReducer.userRoles.canWriteJobs) {
      this.#jobsService.enable();
    } else {
      this.#jobsService.disable();
    }
  }

  getState(stateId: string): Promise<UploaderState | undefined> {
    return this.#stateStorage.get(stateId);
  }

  /** Queues an upload without starting it. */
  queue(request: UploadRequest): Promise<UploaderState> {
    const { fileInfo, options, target, uploadKind } = request;
    return this.#scheduler.queue(fileInfo, options, { target, uploadKind });
  }

  /**
   * Starts or resumes an upload and settles once it stops. A failure is
   * reported through the upload state rather than by rejecting.
   */
  async run(stateId: string): Promise<void> {
    this.#syncJobsService();
    try {
      await this.#scheduler.run(stateId);
    } catch (error) {
      logger.debug(`Upload ${stateId} failed`, error);
    }
  }

  async pause(stateId: string): Promise<void> {
    await this.#scheduler.pause(stateId);
  }

  /**
   * Stops the upload and deletes what it created in Iconik. The upload stays
   * on the list, as if it had just been queued.
   */
  async cancel(stateId: string): Promise<void> {
    await this.#scheduler.cancel(stateId);
  }

  /** Takes an upload off the list, undoing it unless it has completed. */
  async remove(stateId: string): Promise<void> {
    const state = await this.#stateStorage.get(stateId);
    await this.#scheduler.remove(stateId);
    if (state?.fileInfo.streamSource.type === 'fileRef') {
      unregisterFile(state.fileInfo.streamSource.id);
      deleteFileComponents(state.fileInfo.streamSource.id);
    }
  }

  rename(
    stateId: string,
    customFileName: string,
  ): Promise<UploaderState | undefined> {
    return this.#scheduler.rename(stateId, customFileName);
  }

  update(
    stateId: string,
    update: { options?: Partial<UploadOptions>; targetPath?: string },
  ): Promise<UploaderState | undefined> {
    return this.#scheduler.update(stateId, update);
  }

  async setAssetTitle(stateId: string, title: string): Promise<void> {
    const state = await this.#scheduler.update(stateId, {
      options: { customAssetTitle: title },
    });
    const assetId = (state ?? (await this.getState(stateId)))?.target.assetId;
    if (assetId) {
      await this.#store.dispatch(httpPatchAssetsByAssetId(assetId, { title }));
    }
  }

  destroy(): void {
    this.#sideEffects.detach();
    this.#jobsService.disable();
  }
}

let uploaderService: UploaderService | undefined;

/**
 * Creates the service for the app's store. It is kept out of the store module
 * itself, since the reducers the store imports reach this engine.
 */
export const initUploaderService = (
  store: UploadSideEffectsStore,
): UploaderService => {
  uploaderService ??= new UploaderService(store);
  return uploaderService;
};

export const getUploaderService = (): UploaderService => {
  if (!uploaderService) {
    throw new Error('The uploader service has not been initialized');
  }
  return uploaderService;
};
